/**
 * Spells your gear casts for you.
 *
 * 99 stats in this pack carry a `proc_spell` effect: "15% chance to cast Armageddon on hit",
 * "cast Chain Lightning when you crit", "Blood Explosion on kill". They are a real and
 * sometimes dominant share of a build's damage, and nothing was counting them.
 *
 * ## Why the chance is not computed here
 *
 * Every proc is gated, and the gates are the interesting part:
 *
 *     proc_armageddon_on_attack_hit
 *       ifs: is_is_dodged_true_is_false, random_roll, style_is_int_is_false,
 *            is_hit_or_bonus, is_armageddon_not_on_cd
 *
 * — a roll, a check that the hit landed, a check that the skill is not a spell, and a cooldown.
 * `spell_has_tag_ricochet_shot` on another one restricts it to a single skill; a dozen more use
 * `spell_has_tag_not_X` to stop a spell procing itself.
 *
 * All of that is already ported, in `conditions.ts`, and already runs: the damage sweep
 * evaluates each block's `ifs` into a weight before handing it to `applyStatEffect`. So the
 * chance a proc fires *on this skill's hit* falls out of the existing pass, and this file never
 * re-derives it. `effects.ts` records the block, `simulate.ts` returns the list, and the work
 * here is the two things the sweep cannot know: how often the trigger happens, and what the
 * procced spell does when it lands.
 *
 * ## The rate
 *
 * A proc on hit fires at `hits per second × chance`, and is then capped:
 *
 *     DEFAULT_PROC_COOLDOWN_TICKS = 20
 *
 * `SpellConfiguration.proc_cooldown_ticks` is declared by 429 spells, and the
 * `is_<spell>_not_on_cd` condition on almost every proc block is what enforces it. A proc whose
 * spell has a one-second cooldown cannot fire more than once a second however fast you attack,
 * which is what stops a fast skill from turning a 5% proc into the whole build.
 *
 * Hits per second is this skill's own — every source's landed hits over the cast cycle, which
 * `dps.ts` has already counted. That makes a proc's rate a function of the skill you are
 * measuring, which is correct and is why the same gear procs differently under a channel than
 * under a slow slam.
 *
 * ## Listing what cannot fire
 *
 * The sweep only reaches a block whose conditions left it above zero, so a proc that *cannot*
 * trigger on this skill never arrives — and "no procs" is indistinguishable from "no proc gear".
 * Those are different answers, and the second one is the one a player wants when they are
 * choosing a skill. So the sheet is scanned separately for every stat carrying a `proc_spell`,
 * and anything the sweep did not reach is listed at zero with the reason read off its own `ifs`:
 * a basic-attack-only proc, a defensive one, a kill one, or a `spell_has_tag` this skill fails.
 *
 * ## What is not modelled
 *
 * `on_mob_kill` and `on_death` procs have no rate a single-target figure can give them: they
 * fire when something dies, and how often that is depends on the pack, not the build. They are
 * listed with a rate of 0 and a reason, not dropped.
 *
 * A `when_hit` proc — the `Target`-side blocks, "when you are hit, cast X" — is defensive, and
 * how often you are hit is not a property of your build either. Same treatment.
 *
 * Procs of procs are not followed. The procced spell's own DPS is computed with its procs off,
 * which stops a two-spell cycle from recursing and costs nothing real: nothing in the pack
 * procs a spell that procs back.
 */
import { CATEGORY, entry } from "@cte2/schema";
import { DEFAULT_PROC_COOLDOWN_TICKS, TICKS_PER_SECOND } from "./spell-calc.js";
/** How many distinct procs are resolved for a build, so a pathological sheet cannot stall a UI. */
const MAX_PROCS = 24;
const EMPTY = new Set();
/**
 * A procced spell's cast, read off its nested `simulateDps`.
 *
 * The crit chance is backed out of the three totals rather than read off `result.hit`, which is
 * only the largest source: on a multi-source spell that differ in crit, the blend of the whole
 * cast is what `damagePerCast` was made of.
 */
export function procCastOf(result) {
    if (result === undefined)
        return { damage: 0, critChance: 0, critDamage: 0 };
    const cast = directCastOf(result);
    // A summon's cast deals nothing itself; what it is worth is the pets, and a proc-summoned pet is
    // on no clock of the summoning skill's — so one proc is worth their whole life. Whether the
    // summoning cast "crit" changes nothing about them, so the same amount goes on both branches
    // and the blend is untouched.
    const summoned = (result.summons ?? []).filter((s) => s.damagePerSummon !== undefined);
    if (summoned.length === 0)
        return cast;
    const petDamage = summoned.reduce((sum, s) => sum + s.damagePerSummon, 0);
    return {
        damage: cast.damage + petDamage,
        critChance: cast.damage > 0 ? cast.critChance : 0,
        critDamage: cast.critDamage + petDamage,
        pets: {
            perProc: summoned.reduce((sum, s) => sum + s.petsPerCast, 0),
            lifeSeconds: Math.max(...summoned.map((s) => s.lifeSeconds)),
        },
    };
}
function directCastOf(result) {
    const damage = result.damagePerCast;
    const critDamage = result.critDamagePerCast;
    // average = normal + c·(crit − normal) per source, so each source's normal hit comes back out
    // of its two totals; then the same identity over the whole cast gives the blended c.
    const normal = result.sources.reduce((sum, s) => {
        const c = s.hit.critChance;
        return sum + (c >= 1 ? s.damagePerCast : (s.damagePerCast - c * s.critDamagePerCast) / (1 - c));
    }, 0);
    const critChance = critDamage > normal
        ? Math.min(1, Math.max(0, (damage - normal) / (critDamage - normal)))
        : result.hit.critChance;
    return { damage, critChance, critDamage };
}
/**
 * Every proc this build can fire while casting this skill, with its rate and its damage.
 *
 * The two sweeps are blended by crit chance the same way the hit is: a proc gated on
 * `is_crit_true` resolves to 0 in the non-crit branch and its roll in the crit one, so the
 * honest per-hit chance is the weighted mix rather than either branch alone.
 */
export function resolveProcs(input) {
    const blended = blend(input.onHit, input.onCrit, input.critChance);
    // Everything the build carries, so a proc the sweep never reached is still named. The sweep's
    // answer wins wherever the two overlap: it is the ported condition pass, this is a scan.
    for (const candidate of candidates(input.snapshot, input.sheet)) {
        const key = `${candidate.statId}:${candidate.spellId}`;
        const reached = blended.get(key);
        if (reached === undefined)
            blended.set(key, { ...candidate, chance: 0 });
        // The sweep reports a chance and nothing else, so what the block *spends* is read off the
        // scan even for a proc the sweep reached. Only that: the scan's `ifs` would change which
        // limit a reached proc reports, and the sweep's answer is the one that wins there.
        else {
            if (candidate.consumes !== undefined)
                reached.consumes = candidate.consumes;
            if (candidate.targetNeeds !== undefined)
                reached.targetNeeds = candidate.targetNeeds;
        }
    }
    // One answer per debuff, however many procs spend it: two procs sharing one supply are not
    // each given the whole of it, but splitting it between them is a question about which fires
    // first, and nothing in the pack has two consumers of the same debuff on one sheet.
    const supplies = new Map();
    const supplyFor = (effectId) => {
        if (input.supplyOf === undefined)
            return undefined;
        let supply = supplies.get(effectId);
        if (supply === undefined) {
            supply = input.supplyOf(effectId);
            supplies.set(effectId, supply);
        }
        return supply;
    };
    const out = [];
    const hits = [...blended.values()].sort((a, b) => b.chance - a.chance).slice(0, MAX_PROCS);
    for (const hit of hits) {
        const cooldownTicks = procCooldownOf(input.snapshot, hit.spellId);
        const capPerSecond = TICKS_PER_SECOND / Math.max(1, cooldownTicks);
        let limit = limitOf(hit, input.spellTags, input.disabledSpells ?? EMPTY, input.incomingPerSecond);
        const supply = hit.consumes === undefined ? undefined : supplyFor(hit.consumes.effectId);
        const supplyCap = supply === undefined || hit.consumes === undefined
            ? Number.POSITIVE_INFINITY
            : supply.stacksPerSecond / hit.consumes.stacks;
        if (limit === undefined && supply !== undefined && supply.basis === "none")
            limit = "no-supply";
        const triggersPerSecond = limit === undefined ? triggerRateOf(hit, input) : 0;
        const triggered = triggersPerSecond * hit.chance;
        const perSecond = Math.min(triggered, capPerSecond, supplyCap);
        const boundBy = perSecond <= 0
            ? undefined
            : perSecond === supplyCap && supplyCap < triggered
                ? "supply"
                : perSecond === capPerSecond && capPerSecond < triggered
                    ? "cooldown"
                    : "trigger";
        const answer = perSecond > 0 ? input.damageOf(hit.spellId, hit.position) : 0;
        const cast = typeof answer === "number" ? undefined : answer;
        const damagePerProc = typeof answer === "number" ? answer : answer.damage;
        out.push({
            statId: hit.statId,
            spellId: hit.spellId,
            position: hit.position,
            chance: hit.chance,
            cooldownTicks,
            triggersPerSecond,
            perSecond,
            ...(boundBy === undefined ? {} : { boundBy }),
            ...(supply === undefined || hit.consumes === undefined
                ? {}
                : { consumes: { effectId: hit.consumes.effectId, stacksPerProc: hit.consumes.stacks, supply } }),
            damagePerProc,
            // A pure summon's pets do not crit on the summoning cast, so it has no crit to show.
            ...(cast === undefined || cast.damage <= 0 || (cast.pets !== undefined && cast.critChance <= 0)
                ? {}
                : { critChance: cast.critChance, critDamagePerProc: cast.critDamage }),
            dps: damagePerProc * perSecond,
            ...(cast?.pets === undefined ? {} : { pets: { ...cast.pets, alive: 0 } }),
            ...(limit === undefined
                ? damagePerProc <= 0 && perSecond > 0
                    ? { limit: "no-damage" }
                    : {}
                : { limit }),
            ...(limit === "wrong-skill" && hit.needsTag !== undefined ? { needsTag: hit.needsTag } : {}),
        });
    }
    // A debuff one proc spends is up for another only on the triggers that land before it is spent.
    // `out` is still parallel to `hits` here.
    out.forEach((proc, i) => {
        const hit = hits[i];
        if (hit.targetNeeds === undefined || hit.consumes !== undefined || proc.perSecond <= 0)
            return;
        for (const effectId of hit.targetNeeds) {
            const spender = out.find((o) => o.consumes?.effectId === effectId && o.boundBy === "supply" && o.chance > 0);
            if (spender === undefined)
                continue;
            // Each firing of the spender is one trigger that found the debuff; at a chance below 1,
            // the triggers that found it and rolled nothing leave it for the next.
            const finding = spender.perSecond / spender.chance;
            const current = out[i];
            if (finding >= current.triggersPerSecond)
                continue;
            const capPerSecond = TICKS_PER_SECOND / Math.max(1, current.cooldownTicks);
            const triggered = finding * current.chance;
            const perSecond = Math.min(triggered, capPerSecond);
            out[i] = {
                ...current,
                perSecond,
                boundBy: perSecond < triggered ? "cooldown" : "supply",
                dps: current.damagePerProc * perSecond,
                competes: { effectId, spentBy: spender.statId },
            };
        }
    });
    const shared = shareCooldowns(out);
    shared.sort((a, b) => b.dps - a.dps);
    for (const proc of shared) {
        if (proc.limit !== "on-kill" && proc.limit !== "when-hit")
            continue;
        input.diagnostics.push({
            severity: "info",
            code: "proc-rate-unknown",
            path: "skills",
            message: proc.limit === "on-kill"
                ? `\`${proc.statId}\` casts \`${proc.spellId}\` on a kill, and how often something dies ` +
                    `depends on the pack rather than on the build. It is listed and not counted.`
                : `\`${proc.statId}\` casts \`${proc.spellId}\` when you are hit, and this build does not ` +
                    `say how often that is. State the enemy's attack damage and how often it swings on ` +
                    `the Config tab (an attacker profile fills both) and this gets a real rate. It is ` +
                    `listed and not counted until then.`,
        });
    }
    return shared;
}
/**
 * One cooldown per procced spell, however many stats roll for it.
 *
 * `ProcSpellEffect.activate` (6.4.13 jar) checks and stamps `procCooldownKey(spell.GUID())` — a
 * key built from the *spell*, not the stat — before it casts. So Arachnid Inoculation's
 * `proc_summon_spider` and `proc_summon_spider_crit` are two rolls against one 10-tick
 * cooldown, and together they summon at most two spiders a second, not four. Rating each stat
 * against the cooldown on its own counted every spider twice.
 *
 * Two procs on the same trigger roll on the same hit, and the first to fire blocks the other, so
 * their combined rate is the union `t × (1 − Π(1 − cᵢ))` rather than the sum. On different
 * clocks (a swing proc and a when-hit proc) the events are separate and the rates add. The
 * shared rate is then split in proportion to each proc's own, which is a guess at who wins the
 * race and does not move the total.
 */
export function shareCooldowns(procs) {
    const bySpell = new Map();
    procs.forEach((proc, i) => {
        if (proc.limit !== undefined || proc.perSecond <= 0)
            return;
        const list = bySpell.get(proc.spellId) ?? [];
        list.push(i);
        bySpell.set(proc.spellId, list);
    });
    const out = procs.map((proc) => proc.pets === undefined
        ? proc
        : { ...proc, pets: { ...proc.pets, alive: proc.perSecond * proc.pets.perProc * proc.pets.lifeSeconds } });
    for (const members of bySpell.values()) {
        if (members.length < 2)
            continue;
        const group = members.map((i) => out[i]);
        const capPerSecond = TICKS_PER_SECOND / Math.max(1, group[0].cooldownTicks);
        // What each would fire at with the cooldown lifted — the supply still binds a consumer.
        const free = group.map((p) => p.boundBy === "cooldown" ? p.triggersPerSecond * p.chance : p.perSecond);
        const freeTotal = free.reduce((sum, r) => sum + r, 0);
        const sameClock = group.every((p) => p.boundBy !== "supply") &&
            group.every((p) => Math.abs(p.triggersPerSecond - group[0].triggersPerSecond) < 1e-9);
        const combined = sameClock
            ? group[0].triggersPerSecond * (1 - group.reduce((miss, p) => miss * (1 - Math.min(1, p.chance)), 1))
            : freeTotal;
        const total = Math.min(combined, capPerSecond);
        if (freeTotal <= 0)
            continue;
        members.forEach((i, k) => {
            const proc = out[i];
            const perSecond = (total * free[k]) / freeTotal;
            out[i] = {
                ...proc,
                perSecond,
                ...(total < combined ? { boundBy: "cooldown" } : {}),
                dps: proc.damagePerProc * perSecond,
                ...(proc.pets === undefined
                    ? {}
                    : { pets: { ...proc.pets, alive: perSecond * proc.pets.perProc * proc.pets.lifeSeconds } }),
            };
        });
    }
    return out;
}
/** The total a rotation's procs add, for the headline. */
export function procDps(procs) {
    return procs.reduce((sum, proc) => sum + proc.dps, 0);
}
/**
 * Every proc the whole rotation fires, merged across the skills that trigger it.
 *
 * Merging rather than summing is the point, and `proc_cooldown_ticks` is why. Two skills that
 * each proc Armageddon at 1/s do not proc it twice a second if Armageddon may only go off once
 * a second — the cooldown is one ceiling over the build, not one per skill, so it has to be
 * applied to the combined trigger rate and never to each skill's own.
 *
 * The chance is weighted by triggers rather than averaged. A `spell_has_tag` gate makes the same
 * stat a 25% proc on one skill and a dead one on another, and the honest per-trigger chance is
 * the mix the rotation actually produces — which is the fast skill's chance when the fast skill
 * is doing the triggering.
 *
 * A limit survives only when *every* skill in the pass is limited: one skill that can trigger a
 * proc is enough to make it a real contributor, and reporting it as "only basic attacks trigger
 * it" because the other three cannot would be false.
 */
export function rotationProcs(entries, rotationSeconds) {
    const merged = new Map();
    for (const entry of entries) {
        for (const proc of entry.procs) {
            const key = `${proc.statId}:${proc.spellId}`;
            let acc = merged.get(key);
            if (acc === undefined) {
                acc = {
                    statId: proc.statId,
                    spellId: proc.spellId,
                    ...(proc.position === undefined ? {} : { position: proc.position }),
                    cooldownTicks: proc.cooldownTicks,
                    triggersPerPass: 0,
                    weighted: 0,
                    bestChance: 0,
                    damagePerProc: 0,
                    limits: [],
                };
                merged.set(key, acc);
            }
            const triggers = proc.triggersPerSecond * entry.cycleSeconds * entry.presses;
            acc.triggersPerPass += triggers;
            acc.weighted += triggers * proc.chance;
            acc.bestChance = Math.max(acc.bestChance, proc.chance);
            // The same spell cast by the same build, so the skills that resolved it agree; the ones
            // that could not trigger it report 0 and must not drag the figure down.
            if (proc.damagePerProc > acc.damagePerProc) {
                acc.damagePerProc = proc.damagePerProc;
                if (proc.critChance !== undefined && proc.critDamagePerProc !== undefined) {
                    acc.crit = { chance: proc.critChance, damage: proc.critDamagePerProc };
                }
                if (proc.pets !== undefined)
                    acc.pets = proc.pets;
            }
            acc.limits.push(proc.limit);
            if (acc.consumes === undefined && proc.consumes !== undefined)
                acc.consumes = proc.consumes;
            if (acc.needsTag === undefined && proc.needsTag !== undefined)
                acc.needsTag = proc.needsTag;
        }
    }
    const out = [];
    for (const acc of merged.values()) {
        const triggersPerSecond = rotationSeconds > 0 ? acc.triggersPerPass / rotationSeconds : 0;
        const chance = acc.triggersPerPass > 0 ? acc.weighted / acc.triggersPerPass : acc.bestChance;
        const capPerSecond = TICKS_PER_SECOND / Math.max(1, acc.cooldownTicks);
        // The supply is one ceiling over the build for the same reason the cooldown is.
        const supplyCap = acc.consumes === undefined
            ? Number.POSITIVE_INFINITY
            : acc.consumes.supply.stacksPerSecond / acc.consumes.stacksPerProc;
        const triggered = triggersPerSecond * chance;
        const perSecond = Math.min(triggered, capPerSecond, supplyCap);
        const limit = acc.limits.some((l) => l === undefined) ? undefined : worstLimit(acc.limits);
        out.push({
            statId: acc.statId,
            spellId: acc.spellId,
            ...(acc.position === undefined ? {} : { position: acc.position }),
            chance,
            cooldownTicks: acc.cooldownTicks,
            triggersPerSecond,
            perSecond: limit === undefined ? perSecond : 0,
            ...(limit === undefined && perSecond > 0
                ? {
                    boundBy: perSecond === supplyCap && supplyCap < triggered
                        ? "supply"
                        : perSecond === capPerSecond && capPerSecond < triggered
                            ? "cooldown"
                            : "trigger",
                }
                : {}),
            ...(acc.consumes === undefined ? {} : { consumes: acc.consumes }),
            ...(acc.pets === undefined ? {} : { pets: acc.pets }),
            damagePerProc: acc.damagePerProc,
            ...(acc.crit === undefined
                ? {}
                : { critChance: acc.crit.chance, critDamagePerProc: acc.crit.damage }),
            dps: limit === undefined ? acc.damagePerProc * perSecond : 0,
            ...(limit === undefined ? {} : { limit }),
            ...(limit === "wrong-skill" && acc.needsTag !== undefined ? { needsTag: acc.needsTag } : {}),
        });
    }
    return shareCooldowns(out).sort((a, b) => b.dps - a.dps);
}
/**
 * Which reason to show when several skills each had one.
 *
 * Ordered by how much it tells the reader: a switch they flipped beats a gate they did not know
 * about, and both beat "its conditions did not hold".
 */
const LIMIT_ORDER = [
    "disabled",
    "wrong-skill",
    "basic-attack",
    "spell-only",
    "when-hit",
    "on-kill",
    "no-supply",
    "no-damage",
    "cannot-trigger",
];
function worstLimit(limits) {
    for (const candidate of LIMIT_ORDER) {
        if (limits.includes(candidate))
            return candidate;
    }
    return "cannot-trigger";
}
/**
 * One entry per (stat, spell), with the two branches weighted by crit chance.
 *
 * Keyed on both because one stat can proc two spells and two stats can proc the same one —
 * `proc_blood_explosion_on_crit` and `proc_blood_explosion_bleed` are different gear doing the
 * same thing, and they share a cooldown but not a chance.
 */
function blend(onHit, onCrit, critChance) {
    const out = new Map();
    const fold = (hits, weight) => {
        for (const hit of hits) {
            const key = `${hit.statId}:${hit.spellId}`;
            const existing = out.get(key);
            if (existing)
                existing.chance += hit.chance * weight;
            else {
                out.set(key, {
                    statId: hit.statId,
                    spellId: hit.spellId,
                    side: hit.side,
                    position: hit.position === "TARGET" ? "TARGET" : "CASTER",
                    events: [],
                    ifs: [],
                    chance: hit.chance * weight,
                });
            }
        }
    };
    fold(onHit, 1 - critChance);
    fold(onCrit, critChance);
    return out;
}
/**
 * Why a proc contributes nothing, read off its own block.
 *
 * Order matters: a defensive proc is defensive whatever else is true of it, and a proc that
 * belongs to another skill is more usefully reported as such than as "conditions failed".
 */
function limitOf(hit, spellTags, disabledSpells, 
/** Undefined when no attacker is stated, which is what keeps `when-hit` meaning something. */
incomingPerSecond) {
    // The player's own switch, so it is asked before anything derived.
    if (disabledSpells.has(hit.spellId))
        return "disabled";
    // Deliberately still `when-hit` even with an attacker stated, and asked *before* the plain
    // `Target`-side case below, because one of these is a `Target`-side proc too.
    //
    // A proc gated on the block or the dodge fires on the branch that *happened*, so its rate is
    // the incoming rate times that chance alone — and block, dodge and spell dodge are not
    // separable from the outside: the sweep folds all three into one avoidance outcome, and
    // splitting them would mean re-deriving the `DodgeRating` and `SpellDodgeEffect` curves here,
    // against this file's whole premise that it never re-derives what the sweep already answered.
    // Reported with its reason rather than given a rate that would silently be the wrong one.
    if (hit.ifs.includes("is_is_blocked_true") || hit.ifs.includes("is_is_dodged_true")) {
        return "when-hit";
    }
    // A `Target`-side block is one the *enemy's* hit on you triggers — which now has a rate, when
    // the document says how often that happens.
    if (hit.side === "Target")
        return incomingPerSecond === undefined ? "when-hit" : undefined;
    if (hit.events.includes("on_mob_kill") || hit.events.includes("on_death"))
        return "on-kill";
    if (hit.chance > 0)
        return undefined;
    if (hit.needsTag !== undefined && !spellTags.has(hit.needsTag))
        return "wrong-skill";
    if (hit.ifs.includes("is_is_basic_atk_true"))
        return "basic-attack";
    if (hit.ifs.includes("is_is_basic_atk_true_is_false"))
        return "spell-only";
    return "cannot-trigger";
}
/**
 * How often this proc's trigger happens, per second.
 *
 * Two clocks, and which one a proc is on is a property of the block rather than of the skill:
 * your hits for an ordinary offensive proc, the enemy's hits for a `Target`-side one.
 *
 * A proc that fires on the *avoidance* — "when you block", "when you dodge" — is on a third
 * clock this cannot give it, and {@link limitOf} keeps it at `when-hit` for that reason.
 */
function triggerRateOf(hit, input) {
    const incoming = input.incomingPerSecond;
    if (hit.side === "Target" && incoming !== undefined)
        return incoming;
    return input.hitsPerSecond;
}
/**
 * Which stats cast `spellId`, and whether the build has any of them.
 *
 * The inverse of what the rest of this module does. `resolveProcs` starts from the sheet and
 * asks what it casts; this starts from a spell and asks what would cast it — which is the only
 * way to say anything honest about a spell a player cannot press.
 *
 * The pack's 20 `cursed_*` variants, the curses, `mirror_image`, `power_surge`, `martyrdom`,
 * `eighth_gate` and `blood_harvest` all declare `cast_speed_ticks: 0`. Asked for one directly,
 * the engine has no cast rate to pace it with, and the honest answer is not a number — it is
 * "this happens when `<stat>` fires, and here is whether you have it".
 *
 * `onSheet` is the distinction that matters: a build carrying the stat gets a real answer from
 * `DpsResult.procs` while it is casting something else, and a build carrying none is being shown
 * a spell it has no way to produce.
 */
export function procSourcesFor(snapshot, spellId, sheet) {
    const out = [];
    const effects = snapshot.registries[CATEGORY.statEffect] ?? {};
    for (const [statId, stat] of Object.entries(snapshot.registries[CATEGORY.stat] ?? {})) {
        const blocks = stat.data["effect"];
        if (!Array.isArray(blocks))
            continue;
        for (const raw of blocks) {
            const ids = raw["effects"];
            if (!Array.isArray(ids))
                continue;
            for (const id of ids) {
                if (typeof id !== "string")
                    continue;
                const data = effects[id]?.data;
                if (data?.["ser"] !== "proc_spell")
                    continue;
                if (data["spellId"] !== spellId)
                    continue;
                if (!out.some((o) => o.statId === statId)) {
                    out.push({ statId, onSheet: (sheet.get(statId)?.value ?? 0) > 0 });
                }
            }
        }
    }
    return out;
}
/**
 * Every `proc_spell` the sheet carries, whether or not this skill can trigger it.
 *
 * The same walk `effect-state.ts` does for `give_exile_effect`: a stat above zero whose effect
 * block names a `proc_spell` is a spell the build can cast without pressing anything.
 */
function candidates(snapshot, sheet) {
    const out = [];
    const effects = snapshot.registries[CATEGORY.statEffect] ?? {};
    for (const [statId, stat] of Object.entries(snapshot.registries[CATEGORY.stat] ?? {})) {
        if ((sheet.get(statId)?.value ?? 0) <= 0)
            continue;
        const blocks = stat.data["effect"];
        if (!Array.isArray(blocks))
            continue;
        for (const raw of blocks) {
            const block = raw;
            const ids = Array.isArray(block["effects"]) ? block["effects"] : [];
            const ifs = (Array.isArray(block["ifs"]) ? block["ifs"] : []).filter((i) => typeof i === "string");
            const events = (Array.isArray(block["events"]) ? block["events"] : []).filter((e) => typeof e === "string");
            // `spell_has_tag_not_<tag>` is a negated gate registered under its own id, so only the
            // positive form restricts the proc to a skill.
            const tagGate = ifs.find((i) => i.startsWith("spell_has_tag_") && !i.includes("_not_") && !i.endsWith("_is_false"));
            const needsTag = tagGate === undefined ? undefined : tagFor(snapshot, tagGate);
            const consumes = consumedBy(effects, ids);
            const targetNeeds = targetGatesOf(snapshot, ifs);
            for (const id of ids) {
                if (typeof id !== "string")
                    continue;
                const data = effects[id]?.data;
                if (data === undefined || data["ser"] !== "proc_spell")
                    continue;
                const spellId = data["spellId"];
                if (typeof spellId !== "string")
                    continue;
                out.push({
                    statId,
                    spellId,
                    side: typeof block["side"] === "string" ? block["side"] : "Source",
                    position: data["pos"] === "TARGET" ? "TARGET" : "CASTER",
                    events,
                    ifs,
                    ...(needsTag === undefined ? {} : { needsTag }),
                    ...(consumes === undefined ? {} : { consumes }),
                    ...(targetNeeds.length === 0 ? {} : { targetNeeds }),
                });
            }
        }
    }
    return out;
}
/** The effects a block's `is_under_exile_effect` gates need the target to hold. Negated ones don't count. */
function targetGatesOf(snapshot, ifs) {
    const out = [];
    for (const id of ifs) {
        const data = entry(snapshot, CATEGORY.statCondition, id)?.data;
        if (data?.["ser"] !== "is_under_exile_effect" || data["side"] !== "Target")
            continue;
        if (data["is"] === false)
            continue;
        const effect = data["effect"];
        if (typeof effect === "string" && !out.includes(effect))
            out.push(effect);
    }
    return out;
}
/**
 * The debuff a proc block takes off the target when it fires, if it takes one.
 *
 * `RemoveExileEffect` with `remove_from: Target` sits in the same `effects` list as the
 * `proc_spell`, so it runs on exactly the hits the proc does. Only the target side is read: a
 * block that removes something from *you* is not spending a resource another skill supplies.
 */
function consumedBy(effects, ids) {
    for (const id of ids) {
        if (typeof id !== "string")
            continue;
        const data = effects[id]?.data;
        if (data?.["ser"] !== "remove_exile_effect" || data["remove_from"] !== "Target")
            continue;
        const effectId = data["effect"];
        if (typeof effectId !== "string")
            continue;
        const stacks = typeof data["stacks"] === "number" && data["stacks"] > 0 ? data["stacks"] : 1;
        return { effectId, stacks };
    }
    return undefined;
}
/** The tag a `spell_has_tag` condition tests for — an object field, not the id's suffix. */
function tagFor(snapshot, conditionId) {
    const data = entry(snapshot, CATEGORY.statCondition, conditionId)?.data;
    const tag = data?.["tag"];
    if (typeof tag === "string")
        return tag;
    if (tag !== null && typeof tag === "object") {
        const id = tag["id"];
        if (typeof id === "string")
            return id;
    }
    return undefined;
}
/**
 * `proc_cooldown_ticks` on the procced spell.
 *
 * The `is_<spell>_not_on_cd` condition every proc block carries is what reads it in game, and
 * that condition is unanswerable from a static document — so the cooldown is applied here, as a
 * ceiling on the rate, rather than as a gate on the roll.
 */
export function procCooldownOf(snapshot, spellId) {
    const config = entry(snapshot, CATEGORY.spell, spellId)?.data?.["config"];
    const value = config !== null && typeof config === "object"
        ? config["proc_cooldown_ticks"]
        : undefined;
    return typeof value === "number" && value > 0 ? value : DEFAULT_PROC_COOLDOWN_TICKS;
}
//# sourceMappingURL=procs.js.map